// src/components/admin/contas/SubContasEditores.tsx
// GRAIA Admin — Editores: grid de cards azuis + modal Criar/Promover
import React, { useEffect, useState, useCallback, useImperativeHandle } from 'react';
import { Plus, X, Pencil, UserPlus, Search, Ban, CalendarDays, ClipboardCheck, BookOpen } from 'lucide-react';
import ConfirmDialog from '../../ConfirmDialog';
import './SubContasEditores.css';

interface Editor {
    _id: string;
    username: string;
    email: string;
    editorConfig: {
        active: boolean;
        grantedAt: string | null;
        allowedDeckIds: string[];
    };
}

type ModalMode = 'create' | 'promote';

const initials = (n: string) => n.slice(0, 2).toUpperCase();
const fmtDate = (d?: string | null) => d ? new Date(d).toLocaleDateString('pt-BR') : '—';
const inputSt = (disabled = false): React.CSSProperties => ({
    padding: '8px 10px', borderRadius: 7, border: '1px solid var(--sub-border)',
    background: 'var(--sub-surface-2)', color: 'var(--sub-text-primary)',
    fontSize: '0.84rem', outline: 'none', width: '100%', boxSizing: 'border-box',
    opacity: disabled ? 0.4 : 1,
});

const SubContasEditores = React.forwardRef<{ openModal: (mode: ModalMode) => void }, {}>((_, ref) => {
    const token = localStorage.getItem('accessToken');
    const [editors, setEditors] = useState<Editor[]>([]);
    const [loading, setLoading] = useState(true);

    const [showModal, setShowModal] = useState(false);
    const [modalMode, setModalMode] = useState<ModalMode>('create');
    const [saving, setSaving] = useState(false);

    // Criar
    const [createForm, setCreateForm] = useState({ username: '', email: '', password: '' });
    const [createError, setCreateError] = useState('');

    // Promover
    const [promoteSearch, setPromoteSearch] = useState('');
    const [promoteResults, setPromoteResults] = useState<{ _id: string; username: string; email: string }[]>([]);
    const [selectedUser, setSelectedUser] = useState<{ _id: string; username: string } | null>(null);
    const [promoteError, setPromoteError] = useState('');

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [confirmUserId, setConfirmUserId] = useState<string | null>(null);

    const fetchEditors = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch('/api/admin/features/editors/list', { headers: { Authorization: `Bearer ${token}` } });
            const data = await res.json();
            if (data.success) setEditors(data.editors);
        } finally { setLoading(false); }
    }, [token]);

    useEffect(() => { fetchEditors(); }, [fetchEditors]);

    const openModal = useCallback((mode: ModalMode) => {
        setModalMode(mode); setShowModal(true);
        setCreateForm({ username: '', email: '', password: '' }); setCreateError('');
        setPromoteSearch(''); setPromoteResults([]); setSelectedUser(null); setPromoteError('');
    }, []);

    useImperativeHandle(ref, () => ({ openModal }), [openModal]);

    const handleCreate = async () => {
        if (!createForm.username || !createForm.email || !createForm.password) {
            setCreateError('Preencha todos os campos.'); return;
        }
        setSaving(true); setCreateError('');
        try {
            const res = await fetch('/api/admin/features/create-editor', {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify(createForm)
            });
            const data = await res.json();
            if (data.success) { setShowModal(false); fetchEditors(); }
            else setCreateError(data.message || 'Erro ao criar editor.');
        } finally { setSaving(false); }
    };

    const searchUsers = useCallback(async (q: string) => {
        if (!q || q.length < 2) { setPromoteResults([]); return; }
        try {
            const res = await fetch(`/api/admin/subscriptions/users?search=${encodeURIComponent(q)}&limit=5`, { headers: { Authorization: `Bearer ${token}` } });
            const data = await res.json();
            if (data.success) setPromoteResults(data.users);
        } catch { }
    }, [token]);

    const handlePromote = async () => {
        if (!selectedUser) return;
        setSaving(true); setPromoteError('');
        try {
            const res = await fetch(`/api/admin/features/${selectedUser._id}/grant-editor`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
                body: JSON.stringify({})
            });
            const data = await res.json();
            if (data.success) { setShowModal(false); fetchEditors(); }
            else setPromoteError(data.message || 'Erro ao promover editor.');
        } finally { setSaving(false); }
    };

    const requestRevoke = (id: string) => { setConfirmUserId(id); setConfirmOpen(true); };
    const confirmRevoke = async () => {
        if (!confirmUserId) return;
        setConfirmOpen(false);
        await fetch(`/api/admin/features/${confirmUserId}/revoke-editor`, { method: 'POST', headers: { Authorization: `Bearer ${token}` } });
        setConfirmUserId(null); fetchEditors();
    };

    return (
        <div>
            <div className="edi-permissions-banner">
                <div className="edi-perm-item"><ClipboardCheck size={14} /> <span>Pode revisar questões em análise</span></div>
                <div className="edi-perm-item"><BookOpen size={14} /> <span>Pode sugerir edições em apostilas</span></div>
                <div className="edi-perm-item"><Pencil size={14} /> <span>Pode editar conteúdo de decks autorizados</span></div>
            </div>

            {loading ? (
                <div className="edi-loading"><div className="edi-spinner" /> Carregando editores...</div>
            ) : editors.length === 0 ? (
                <div className="edi-empty"><div className="edi-empty-icon">✏️</div>Nenhum editor cadastrado ainda.</div>
            ) : (
                <div className="edi-grid">
                    {editors.map(e => (
                        <div key={e._id} className="edi-card">
                            <div className="edi-card-top">
                                <div className="edi-avatar">{initials(e.username)}</div>
                                <div className="edi-card-info">
                                    <div className="edi-card-name">{e.username}</div>
                                    <div className="edi-card-email">{e.email}</div>
                                </div>
                                <span className="edi-badge"><Pencil size={9} /> Editor</span>
                            </div>
                            <div className="edi-card-meta">
                                <div className="edi-meta-row">
                                    <span className="edi-meta-label"><CalendarDays size={10} style={{ verticalAlign: 'middle', marginRight: 3 }} /> Concedido</span>
                                    <span className="edi-meta-value">{fmtDate(e.editorConfig.grantedAt)}</span>
                                </div>
                                <div className="edi-meta-row">
                                    <span className="edi-meta-label">Acesso a decks</span>
                                    <span className={`edi-meta-value ${!e.editorConfig.allowedDeckIds?.length ? 'all-decks' : ''}`}>
                                        {e.editorConfig.allowedDeckIds?.length
                                            ? `${e.editorConfig.allowedDeckIds.length} deck(s)`
                                            : '✓ Todos'}
                                    </span>
                                </div>
                            </div>
                            <button className="edi-revoke-btn" onClick={() => requestRevoke(e._id)}><Ban size={13} /> Revogar Editor</button>
                        </div>
                    ))}
                </div>
            )}

            {showModal && (
                <div className="edi-overlay" onClick={() => setShowModal(false)}>
                    <div className="edi-modal" onClick={e => e.stopPropagation()}>
                        <div className="edi-modal-header">
                            <h3 className="edi-modal-title"><Pencil size={15} style={{ color: 'var(--edi-blue)' }} /> {modalMode === 'create' ? 'Novo Editor' : 'Promover a Editor'}</h3>
                            <button className="edi-modal-close" onClick={() => setShowModal(false)}><X size={15} /></button>
                        </div>
                        <div className="edi-modal-body">
                            <div className="edi-mode-tabs">
                                <button className={`edi-mode-tab ${modalMode === 'create' ? 'active' : ''}`} onClick={() => { setModalMode('create'); setCreateError(''); setPromoteError(''); }}><Plus size={13} /> Criar nova conta</button>
                                <button className={`edi-mode-tab ${modalMode === 'promote' ? 'active' : ''}`} onClick={() => { setModalMode('promote'); setCreateError(''); setPromoteError(''); }}><UserPlus size={13} /> Promover existente</button>
                            </div>

                            {modalMode === 'create' && (<>
                                <div className="edi-form-grid">
                                    <div className="edi-form-group"><label>Nome de usuário *</label><input value={createForm.username} onChange={e => setCreateForm(f => ({ ...f, username: e.target.value }))} placeholder="username" style={inputSt()} /></div>
                                    <div className="edi-form-group"><label>Email *</label><input type="email" value={createForm.email} onChange={e => setCreateForm(f => ({ ...f, email: e.target.value }))} placeholder="email@exemplo.com" style={inputSt()} /></div>
                                    <div className="edi-form-group" style={{ gridColumn: '1 / -1' }}><label>Senha *</label><input type="password" value={createForm.password} onChange={e => setCreateForm(f => ({ ...f, password: e.target.value }))} placeholder="Mínimo 6 caracteres" style={inputSt()} /></div>
                                </div>
                                {createError && <p className="edi-error">✗ {createError}</p>}
                                <div className="edi-modal-footer">
                                    <button className="edi-btn secondary" onClick={() => setShowModal(false)}>Cancelar</button>
                                    <button className="edi-btn primary" onClick={handleCreate} disabled={saving || !createForm.username || !createForm.email || !createForm.password}>{saving ? 'Criando...' : <><Pencil size={13} /> Criar Editor</>}</button>
                                </div>
                            </>)}

                            {modalMode === 'promote' && (<>
                                <div className="edi-form-group" style={{ marginBottom: 8 }}>
                                    <label>Buscar usuário</label>
                                    <div className="edi-search-wrap">
                                        <Search size={14} />
                                        <input style={{ ...inputSt(), paddingLeft: 32 }} value={promoteSearch} onChange={e => { setPromoteSearch(e.target.value); searchUsers(e.target.value); }} placeholder="Digite nome ou email..." />
                                    </div>
                                </div>
                                {promoteResults.length > 0 && !selectedUser && (
                                    <div className="edi-search-results">
                                        {promoteResults.map(u => (
                                            <button key={u._id} className="edi-search-result" onClick={() => { setSelectedUser(u); setPromoteResults([]); setPromoteSearch(''); }}>
                                                <strong>{u.username}</strong><span>{u.email}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                                {selectedUser && (
                                    <div className="edi-selected-chip">
                                        <Pencil size={13} style={{ color: 'var(--edi-blue)', flexShrink: 0 }} />
                                        Selecionado: <strong>{selectedUser.username}</strong>
                                        <button className="edi-chip-clear" onClick={() => { setSelectedUser(null); setPromoteSearch(''); }}><X size={13} /></button>
                                    </div>
                                )}
                                {promoteError && <p className="edi-error">✗ {promoteError}</p>}
                                <div className="edi-modal-footer">
                                    <button className="edi-btn secondary" onClick={() => setShowModal(false)}>Cancelar</button>
                                    <button className="edi-btn primary" onClick={handlePromote} disabled={!selectedUser || saving}>{saving ? 'Promovendo...' : <><Pencil size={13} /> Conceder Role de Editor</>}</button>
                                </div>
                            </>)}
                        </div>
                    </div>
                </div>
            )}

            <ConfirmDialog isOpen={confirmOpen} title="Revogar Editor" message="Tem certeza que deseja revogar a role de editor deste usuário?" confirmText="Revogar" cancelText="Cancelar" onConfirm={confirmRevoke} onCancel={() => { setConfirmOpen(false); setConfirmUserId(null); }} />
        </div>
    );
});

SubContasEditores.displayName = 'SubContasEditores';
export default SubContasEditores;